"use client"

import { useState, useEffect, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { Button } from "../../ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table"
import { Input } from "../../ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select"
import { AddEditSystemUserModal } from "../../modals/add-edit-system-user-modal"
import { apiClient } from "../../lib/apiClient"
import { useToast } from "../../hooks/use-toast"
import { Users, Plus, Edit2, Loader2, ArrowUp, ArrowDown, ArrowUpDown, Filter, X } from "lucide-react"

interface SystemUser {
  userID: number
  isActive: boolean
  employeeID: string | null
  locationID: number | null
  departmentID: number | null
  userRoles: number[]
  userRoleName: string[]
  joiningDate: string | null
  displayName: string
  email: string
  locationName: string
  departmentName: string
  modifiedBy: string
  modifiedOn: string
}

interface Location {
  id: number
  name: string
}

interface Department {
  id: number
  name: string
}

export function SystemUsersPage() {
  const [systemUsers, setSystemUsers] = useState<SystemUser[]>([])
  const [locations, setLocations] = useState<Location[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<SystemUser | null>(null)
  const { toast } = useToast()

  // Sorting state
  const [sortField, setSortField] = useState<keyof SystemUser | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  // Filtering state
  const [filterDisplayName, setFilterDisplayName] = useState('')
  const [filterDepartment, setFilterDepartment] = useState<string>('all')
  const [filterLocation, setFilterLocation] = useState<string>('all')
  const [filterIsActive, setFilterIsActive] = useState<string>('all')

  // Column width state - initial widths to fit 100% screen
  const [columnWidths, setColumnWidths] = useState({
    srNo: 70,
    displayName: 140,
    email: 180,
    employeeID: 110,
    location: 130,
    department: 130,
    roles: 120,
    joiningDate: 110,
    status: 90,
    modifiedBy: 130,
    modifiedOn: 150,
    action: 110,
  })

  const [resizingColumn, setResizingColumn] = useState<string | null>(null)

  // Fetch system users
  const fetchSystemUsers = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get("/Admin/system-user")
      const data = Array.isArray(response.data) ? response.data : []
      
      // Data already includes locationName, departmentName, modifiedBy, modifiedOn from API
      setSystemUsers(data)
    } catch (err: any) {
      console.error("Error fetching system users:", err)
      setError(err?.response?.data?.message || err?.message || "Failed to load system users")
      // Don't show toast on initial load failure, just set error state
    } finally {
      setLoading(false)
    }
  }

  // Fetch locations and departments from lookup values
  const fetchDropdownData = async () => {
    try {
      // Use the Helper/GetLookupValues endpoint with the GUID
      // The GUID appears to be: 3228D5A1-DA07-45AC-8F99-CDE6C239EC6B,739F2F0D-76F0-4FB9-A778-B97AE5173629,E771BF28-E6E9-4263-B9F4-29D80B38C8E0
      const guid = "3228D5A1-DA07-45AC-8F99-CDE6C239EC6B%2C739F2F0D-76F0-4FB9-A778-B97AE5173629%2CE771BF28-E6E9-4263-B9F4-29D80B38C8E0"
      const response = await apiClient.get(`/Helper/GetLookupValues/${guid}`)
      
      console.log("Lookup values response:", response.data)
      
      if (Array.isArray(response.data)) {
        // Filter by categoryName - Locations
        const locationsData = response.data
          .filter((item: any) => item.categoryName === 'Locations')
          .map((item: any) => ({ 
            id: item.value,  // value is the ID
            name: item.key   // key is the display name
          }))
        
        // Filter by categoryName - Departments
        const departmentsData = response.data
          .filter((item: any) => item.categoryName === 'Departments')
          .map((item: any) => ({ 
            id: item.value,  // value is the ID
            name: item.key   // key is the display name
          }))
        
        console.log("Parsed locations:", locationsData)
        console.log("Parsed departments:", departmentsData)
        
        setLocations(locationsData)
        setDepartments(departmentsData)
      } else {
        setLocations([])
        setDepartments([])
      }
    } catch (err: any) {
      console.error("Error fetching lookup values:", err)
      setLocations([])
      setDepartments([])
    }
  }

  useEffect(() => {
    fetchDropdownData().then(() => {
      fetchSystemUsers()
    })
  }, [])

  // Format roles for display
  const formatRoles = (userRoles: number[] | undefined, userRoleName: string[] | undefined) => {
    if (userRoleName && userRoleName.length > 0) {
      return userRoleName.join(', ')
    }
    if (userRoles && userRoles.length > 0) {
      return userRoles.join(', ')
    }
    return "--"
  }

  // Format date and time
  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return "--"
    try {
      const date = new Date(dateString)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return "--"
    }
  }

  // Handle column resizing
  const handleMouseDown = (column: string, e: React.MouseEvent) => {
    e.preventDefault()
    setResizingColumn(column)
    const startX = e.pageX
    const startWidth = columnWidths[column as keyof typeof columnWidths]

    const handleMouseMove = (e: MouseEvent) => {
      const diff = e.pageX - startX
      const newWidth = Math.max(80, startWidth + diff) // Minimum width of 80px
      setColumnWidths(prev => ({
        ...prev,
        [column]: newWidth
      }))
    }

    const handleMouseUp = () => {
      setResizingColumn(null)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Sorting handler
  const handleSort = (field: keyof SystemUser) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Get sort icon
  const getSortIcon = (field: keyof SystemUser) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4 opacity-50" />
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4" /> : 
      <ArrowDown className="h-4 w-4" />
  }

  // Filter and sort data
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = systemUsers.filter(user => {
      const matchesDisplayName = !filterDisplayName || 
        user.displayName?.toLowerCase().includes(filterDisplayName.toLowerCase())
      const matchesDepartment = filterDepartment === 'all' || 
        (user.departmentID && user.departmentID > 0 && user.departmentID.toString() === filterDepartment)
      const matchesLocation = filterLocation === 'all' || 
        (user.locationID && user.locationID > 0 && user.locationID.toString() === filterLocation)
      const matchesIsActive = filterIsActive === 'all' || 
        (filterIsActive === 'active' && user.isActive) ||
        (filterIsActive === 'inactive' && !user.isActive)

      return matchesDisplayName && matchesDepartment && matchesLocation && matchesIsActive
    })

    if (sortField) {
      filtered.sort((a, b) => {
        let aValue: any, bValue: any

        switch (sortField) {
          case 'displayName':
            aValue = (a.displayName || '').toLowerCase()
            bValue = (b.displayName || '').toLowerCase()
            break
          case 'departmentID':
            aValue = a.departmentName?.toLowerCase() || ''
            bValue = b.departmentName?.toLowerCase() || ''
            break
          case 'locationID':
            aValue = a.locationName?.toLowerCase() || ''
            bValue = b.locationName?.toLowerCase() || ''
            break
          case 'isActive':
            aValue = a.isActive ? 1 : 0
            bValue = b.isActive ? 1 : 0
            break
          default:
            aValue = a[sortField]?.toString().toLowerCase() || ''
            bValue = b[sortField]?.toString().toLowerCase() || ''
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortDirection === 'asc' ? aValue - bValue : bValue - aValue
        }

        if (sortDirection === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
        }
      })
    }

    return filtered
  }, [systemUsers, filterDisplayName, filterDepartment, filterLocation, filterIsActive, sortField, sortDirection])

  // Clear filters
  const clearFilters = () => {
    setFilterDisplayName('')
    setFilterDepartment('all')
    setFilterLocation('all')
    setFilterIsActive('all')
  }

  const handleAddClick = () => {
    setEditingUser(null)
    setIsModalOpen(true)
  }

  const handleEditClick = (user: SystemUser) => {
    setEditingUser(user)
    setIsModalOpen(true)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setEditingUser(null)
  }

  const handleSuccess = () => {
    fetchSystemUsers()
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "--"
    try {
      return new Date(dateString).toLocaleDateString()
    } catch {
      return "--"
    }
  }

  if (loading && systemUsers.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
            <p className="text-gray-500">Loading system users...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card className="w-full bg-white border border-gray-200 rounded-lg shadow-sm">
        <CardHeader className="border-b border-gray-200 bg-white p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-600" />
              System Users
            </CardTitle>
            <Button
              onClick={handleAddClick}
              className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 w-full">
          {/* Filters */}
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Filter by Display Name..."
                  value={filterDisplayName}
                  onChange={(e) => setFilterDisplayName(e.target.value)}
                  className="bg-white"
                />
              </div>
              <div className="min-w-[150px]">
                <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id.toString()}>
                        {dept.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[150px]">
                <Select value={filterLocation} onValueChange={setFilterLocation}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id.toString()}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[120px]">
                <Select value={filterIsActive} onValueChange={setFilterIsActive}>
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(filterDisplayName || filterDepartment !== 'all' || filterLocation !== 'all' || filterIsActive !== 'all') && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="flex items-center gap-1"
                >
                  <X className="h-4 w-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          {error && systemUsers.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <Button onClick={fetchSystemUsers} variant="outline">
                Retry
              </Button>
            </div>
          ) : filteredAndSortedUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No system users found.</p>
              <Button onClick={handleAddClick} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="h-4 w-4 mr-2" />
                Add First User
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <Table style={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHeader>
                  <TableRow className="bg-gray-50 border-b border-gray-200">
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.srNo}px`, minWidth: `${columnWidths.srNo}px` }}
                    >
                      Sr. No.
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('srNo', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.displayName}px`, minWidth: `${columnWidths.displayName}px` }}
                    >
                      <Button
                        variant="ghost"
                        className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                        onClick={() => handleSort('displayName')}
                      >
                        Display Name
                        {getSortIcon('displayName')}
                      </Button>
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('displayName', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.email}px`, minWidth: `${columnWidths.email}px` }}
                    >
                      Email
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('email', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.employeeID}px`, minWidth: `${columnWidths.employeeID}px` }}
                    >
                      Employee ID
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('employeeID', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.location}px`, minWidth: `${columnWidths.location}px` }}
                    >
                      <Button
                        variant="ghost"
                        className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                        onClick={() => handleSort('locationID')}
                      >
                        Location
                        {getSortIcon('locationID')}
                      </Button>
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('location', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.department}px`, minWidth: `${columnWidths.department}px` }}
                    >
                      <Button
                        variant="ghost"
                        className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                        onClick={() => handleSort('departmentID')}
                      >
                        Department
                        {getSortIcon('departmentID')}
                      </Button>
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('department', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.roles}px`, minWidth: `${columnWidths.roles}px` }}
                    >
                      Roles
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('roles', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.joiningDate}px`, minWidth: `${columnWidths.joiningDate}px` }}
                    >
                      Joining Date
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('joiningDate', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.status}px`, minWidth: `${columnWidths.status}px` }}
                    >
                      <Button
                        variant="ghost"
                        className="h-auto p-0 font-semibold hover:bg-transparent flex items-center gap-2"
                        onClick={() => handleSort('isActive')}
                      >
                        Status
                        {getSortIcon('isActive')}
                      </Button>
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('status', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.modifiedBy}px`, minWidth: `${columnWidths.modifiedBy}px` }}
                    >
                      Modified By
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('modifiedBy', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.modifiedOn}px`, minWidth: `${columnWidths.modifiedOn}px` }}
                    >
                      Modified On
                      <div
                        className="absolute top-0 right-0 w-1 h-full cursor-col-resize hover:bg-blue-500 bg-transparent"
                        onMouseDown={(e) => handleMouseDown('modifiedOn', e)}
                      />
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 p-4 relative"
                      style={{ width: `${columnWidths.action}px`, minWidth: `${columnWidths.action}px` }}
                    >
                      Action
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedUsers.map((user, index) => {
                    const locationName = user.locationName || (user.locationID && user.locationID > 0 ? locations.find(l => l.id === user.locationID)?.name : null) || null
                    const departmentName = user.departmentName || (user.departmentID && user.departmentID > 0 ? departments.find(d => d.id === user.departmentID)?.name : null) || null
                    const rolesDisplay = formatRoles(user.userRoles, user.userRoleName)
                    
                    return (
                      <TableRow key={user.userID} className="hover:bg-gray-50 border-b border-gray-100">
                        <TableCell className="p-4 text-gray-900 truncate text-center" style={{ width: `${columnWidths.srNo}px` }}>
                          {index + 1}
                        </TableCell>
                        <TableCell className="p-4 font-medium text-gray-900 truncate" style={{ width: `${columnWidths.displayName}px` }} title={user.displayName}>
                          {user.displayName}
                        </TableCell>
                        <TableCell className="p-4 text-gray-700 truncate" style={{ width: `${columnWidths.email}px` }} title={user.email}>
                          {user.email}
                        </TableCell>
                        <TableCell className="p-4 text-gray-700 truncate" style={{ width: `${columnWidths.employeeID}px` }}>
                          {user.employeeID || "--"}
                        </TableCell>
                        <TableCell className="p-4 text-gray-700 truncate" style={{ width: `${columnWidths.location}px` }} title={locationName || "--"}>
                          {locationName || "--"}
                        </TableCell>
                        <TableCell className="p-4 text-gray-700 truncate" style={{ width: `${columnWidths.department}px` }} title={departmentName || "--"}>
                          {departmentName || "--"}
                        </TableCell>
                        <TableCell className="p-4 text-gray-700 truncate" style={{ width: `${columnWidths.roles}px` }} title={rolesDisplay || "--"}>
                          {rolesDisplay || "--"}
                        </TableCell>
                        <TableCell className="p-4 text-gray-700 truncate" style={{ width: `${columnWidths.joiningDate}px` }}>
                          {formatDate(user.joiningDate) || "--"}
                        </TableCell>
                        <TableCell className="p-4 truncate" style={{ width: `${columnWidths.status}px` }}>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.isActive 
                              ? "bg-green-100 text-green-800" 
                              : "bg-red-100 text-red-800"
                          }`}>
                            {user.isActive ? "Active" : "Inactive"}
                          </span>
                        </TableCell>
                        <TableCell className="p-4 text-gray-700 truncate" style={{ width: `${columnWidths.modifiedBy}px` }} title={user.modifiedBy || "--"}>
                          {user.modifiedBy || "--"}
                        </TableCell>
                        <TableCell className="p-4 text-gray-700 truncate" style={{ width: `${columnWidths.modifiedOn}px` }}>
                          {formatDateTime(user.modifiedOn) || "--"}
                        </TableCell>
                        <TableCell className="p-4" style={{ width: `${columnWidths.action}px` }}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditClick(user)}
                            className="flex items-center gap-1"
                          >
                            <Edit2 className="h-4 w-4" />
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddEditSystemUserModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleSuccess}
        initialData={editingUser ? {
          userID: editingUser.userID,
          isActive: editingUser.isActive,
          employeeID: editingUser.employeeID || "",
          locationID: editingUser.locationID && editingUser.locationID > 0 ? editingUser.locationID : null,
          departmentID: editingUser.departmentID && editingUser.departmentID > 0 ? editingUser.departmentID : null,
          // Pass userRoleName (array of role names) as comma-separated string
          newRoles: editingUser.userRoleName?.join(', ') || "",
          joiningDate: editingUser.joiningDate || "",
          displayName: editingUser.displayName,
          email: editingUser.email,
        } : null}
        locations={locations}
        departments={departments}
      />
    </div>
  )
}